import { useState, useCallback, useEffect, useMemo, useRef, type MouseEvent } from 'react';
import { formatPatientDob } from '@/lib/dateUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  User, Calendar, Hash, Shield, FileText, Lock,
  Volume2, Square, ChevronDown, ChevronUp, Sparkles,
  CheckCircle2, Stethoscope, Syringe, Plus, ClipboardList,
  Info, ShieldCheck, Mic, ArrowLeft, Search, Wand2, Circle,
  AlertTriangle,
} from 'lucide-react';
import { DictationScreen } from '@/components/dictation/DictationScreen';
import { AIReconciliationPanel, type SurgeonClarification } from '@/components/dictation/AIReconciliationPanel';
import { ScheduledCase, SurgeonTemplate, CaseCptLine, Laterality, SurgeonFacility } from '@/lib/templates/types';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
// Use this project's Supabase for op note generation
const LOCAL_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const LOCAL_SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
import { useSpeechSynthesis } from '@/hooks/useSpeechSynthesis';
import { normalizeOpNoteResponse, OpNoteResponse } from './smart-builder/opNoteResponseTypes';
import { PostOpReviewDashboard } from './smart-builder/PostOpReviewDashboard';
import { DemoAgenticAssistant } from './demo-agentic/DemoAgenticAssistant';
import { IntraOpDiscoveryModal } from './smart-builder/review/IntraOpDiscoveryModal';
import { GenerationOverlay } from './smart-builder/GenerationOverlay';
import { DenialRiskAlertPanel } from './denial-defender/DenialRiskAlertPanel';
import { useOpNoteRiskAnalysis, type ActionableIssue } from '@/hooks/useOpNoteRiskAnalysis';
import { WoundCareMatrix } from './WoundCareMatrix';
import { isQCode } from '@/lib/authorization/wizardTypes';
import { SurgeonConversationPanel } from './SurgeonConversationPanel';
import {
  buildPostOpConversationPacket,
  type ClinicalQuestion,
} from '@/lib/surgeon-console/proofPacket';
import {
  buildDeterministicShoulderOperativeNote,
  ensureOperativeTemplateBlocks,
} from '@/lib/surgeon-console/operativeNoteTemplateCompiler';
import { isPerfectWorldDemo, normalizeDemoPostOpResponse } from '@/lib/demo/perfectWorldDemo';
import { buildPostOpDemoAgentExperience } from '@/lib/demo/demoAgenticExperience';
import {
  appendSuggestedDictationLine,
  buildGeneratedPostOpDictation,
  buildNcciAwarePostOpDiscoverySuggestions,
  evaluatePostOpDictationCoverage,
  rankPostOpSearchResultsWithNcci,
  type NcciPairRuleInput,
  type PostOpCptCatalogEntry,
  type PostOpCoverageRequirement,
  type PostOpCoverageResult,
} from '@/lib/surgeon-console/postOpComposer';
import { useCptSearch } from '@/hooks/useCptSearch';

interface PostOpNoteStepProps {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  laterality: Laterality | null;
  facility: SurgeonFacility | null;
  cptLines: CaseCptLine[];
  bmiHighComplexity?: boolean;
  initialBmiValue?: number;
  onCaseDeferred?: () => void;
  onDemoResetCase?: (caseId: string) => void;
}

const DUMMY_NARRATIVE = `[Operative narrative will be generated from surgeon dictation and procedure-specific template. Please use the AI-generate or voice dictation feature to populate this section.]`;

type TemplateImplant = {
  qty: number;
  catalog_id?: string;
  description: string;
};

function normalizeTemplateImplants(value: unknown): TemplateImplant[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item): TemplateImplant | null => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      const description = typeof record.description === 'string' ? record.description.trim() : '';
      if (!description) return null;

      return {
        qty: typeof record.qty === 'number' && Number.isFinite(record.qty) ? record.qty : 1,
        catalog_id: typeof record.catalog_id === 'string' ? record.catalog_id : undefined,
        description,
      };
    })
    .filter((item): item is TemplateImplant => Boolean(item));
}

function getPrimaryCptDisplay(cptLines: CaseCptLine[]): string {
  const codes = cptLines.map(line => line.code).filter(Boolean);
  return codes.length ? `CPT ${codes.join(', ')}` : 'CPT pending';
}

function getProcedureSiteLabel(template: SurgeonTemplate): string {
  const normalized = `${template.body_region || ''} ${template.template_name || ''}`.toLowerCase();
  if (normalized.includes('knee')) return 'Knee';
  if (normalized.includes('shoulder')) return 'Shoulder';
  if (normalized.includes('hip')) return 'Hip';
  if (normalized.includes('ankle')) return 'Ankle';
  if (normalized.includes('foot')) return 'Foot';
  if (normalized.includes('spine') || normalized.includes('lumbar') || normalized.includes('cervical')) return 'Spine';
  return template.body_region || 'Procedure Site';
}

function getDefaultPositioning(template: SurgeonTemplate): string {
  const normalized = `${template.body_region || ''} ${template.template_name || ''}`.toLowerCase();
  if (normalized.includes('knee') || normalized.includes('hip') || normalized.includes('ankle') || normalized.includes('foot')) {
    return 'Supine';
  }
  if (normalized.includes('spine') || normalized.includes('lumbar') || normalized.includes('cervical')) {
    return 'Prone';
  }
  if (normalized.includes('shoulder')) {
    return 'Beach Chair';
  }
  return 'Supine';
}

function isUrgentBypassTemplate(template: SurgeonTemplate, selectedCase: ScheduledCase): boolean {
  const metadata = template.metadata || {};
  const text = `${template.template_name} ${selectedCase.scheduled_procedure || ''}`.toLowerCase();
  return metadata.urgent_bypass === true ||
    text.includes('urgent') ||
    text.includes('traumatic') ||
    text.includes('extensor mechanism');
}

function buildDefaultAssistantStatement(template: SurgeonTemplate): string | undefined {
  const metadata = template.metadata || {};
  if (typeof metadata.default_assistant_statement === 'string') {
    return metadata.default_assistant_statement;
  }
  if (metadata.assistant_required === true) {
    return 'The surgical assistant was utilized and essential to the procedure for safe exposure, retraction, visualization, instrumentation, and closure.';
  }
  return undefined;
}

export function PostOpNoteStep({
  selectedCase, template, laterality, facility, cptLines,
  bmiHighComplexity: initialBmi = false,
  initialBmiValue = 42,
  onCaseDeferred,
  onDemoResetCase,
}: PostOpNoteStepProps) {
  const { isSpeaking, handleSpeak } = useSpeechSynthesis();
  const { isDemoMode } = useDemoMode();
  const perfectWorldDemo = isPerfectWorldDemo(isDemoMode, selectedCase.id);

  // Auto-derive BMI from intake (clinical_metrics extracted during pre-auth) so the
  // surgeon never has to re-enter it. Modifier -22 (morbid obesity) is added only
  // when the extracted BMI meets the payer threshold (>= 40). When BMI is unknown
  // or below threshold, the BMI/Mod-22 section is hidden entirely.
  const intakeBmi: number | undefined = (selectedCase as { clinical_metrics?: { bmi?: number | null } }).clinical_metrics?.bmi ?? undefined;
  const MOD_22_BMI_THRESHOLD = 40;
  const bmiQualifiesForMod22 = typeof intakeBmi === 'number' && intakeBmi >= MOD_22_BMI_THRESHOLD;
  const templateImplants = useMemo(
    () => normalizeTemplateImplants((template.metadata || {}).implants),
    [template.metadata],
  );
  const hasTemplateImplants = templateImplants.length > 0;
  const primaryCptDisplay = useMemo(() => getPrimaryCptDisplay(cptLines), [cptLines]);
  const procedureSiteLabel = useMemo(() => getProcedureSiteLabel(template), [template]);
  const defaultPositioning = useMemo(() => getDefaultPositioning(template), [template]);
  const urgentBypassTemplate = useMemo(
    () => isUrgentBypassTemplate(template, selectedCase),
    [template, selectedCase],
  );
  const defaultAssistantStatement = useMemo(
    () => buildDefaultAssistantStatement(template),
    [template],
  );

  const [dictationMode, setDictationMode] = useState(false);
  const [dictationTranscript, setDictationTranscript] = useState<string | null>(null);
  // Surgeon Q&A answers from the AIReconciliationPanel — woven into the bulletproof note.
  const [surgeonClarifications, setSurgeonClarifications] = useState<Array<{
    cpt_code: string;
    question: string;
    answer: string;
    suggested_addendum_language?: string | null;
  }>>([]);
  const [conversationStatements, setConversationStatements] = useState<string[]>([]);
  const [narrativeText] = useState(DUMMY_NARRATIVE);
  const [ebl, setEbl] = useState('5');
  const [opTime, setOpTime] = useState('45');
  // Default isHighComplexity to TRUE when intake BMI qualifies — surgeon can opt out
  // if the case did not in fact require modifier -22 work. Otherwise default to the
  // explicit prop or false, and the entire section is hidden in render.
  const [isHighComplexity, setIsHighComplexity] = useState(bmiQualifiesForMod22 || initialBmi);
  const [bmiValue, setBmiValue] = useState(intakeBmi ?? initialBmiValue);
  const [useTemplateImplants, setUseTemplateImplants] = useState(hasTemplateImplants);
  const [showModify, setShowModify] = useState(false);
  const [varianceText, setVarianceText] = useState('');
  const [savePermanent, setSavePermanent] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayMinElapsed, setOverlayMinElapsed] = useState(false);
  const overlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [opNoteResponse, setOpNoteResponse] = useState<OpNoteResponse | null>(null);
  const [pendingResponse, setPendingResponse] = useState<OpNoteResponse | null>(null);
  const [discoverySearch, setDiscoverySearch] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [reviewInputState, setReviewInputState] = useState({
    ebl,
    operativeTime: opTime,
    isHighComplexity,
  });
  const [ncciPairRules, setNcciPairRules] = useState<NcciPairRuleInput[] | null>([]);
  const [showReview, setShowReview] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);

  // ── Wound Care Matrix gate (skin substitute Q-codes & 15002-series) ──
  // Read the authorized wound size from template.metadata.cpt_bundle (source
  // of truth from authorization_requests.cpt_bundle). Falls back to a default
  // unit count for legacy Q-codes that don't carry sq-cm in the bundle.
  const cptBundle = ((template?.metadata as any)?.cpt_bundle || []) as Array<{
    code: string; modifier?: string; units?: number; wound_size_sq_cm?: number;
  }>;
  const woundEntry = cptBundle.find(
    e => isQCode(e.code) || e.code === '15002' || e.code === '15003',
  );
  const qCodeLine = cptLines.find(l => isQCode(l.code) || l.code === '15002' || l.code === '15003');
  const qCodeBilledUnits = woundEntry?.wound_size_sq_cm
    ?? woundEntry?.units
    ?? (qCodeLine ? 20 : 0);
  const [woundCareValid, setWoundCareValid] = useState(!qCodeLine);
  const handleWoundCareValidate = useCallback((valid: boolean) => {
    setWoundCareValid(valid);
  }, []);
  const [addedDiscoveries, setAddedDiscoveries] = useState<{ cpt: string; name: string; noteText: string }[]>([]);
  const [ancillaryToggles, setAncillaryToggles] = useState<Record<string, boolean>>({
    '29826': true,
    '29824': true,
    '29828': true,
  });
  const { isAnalyzing: isAuditingNote, result: opNoteRiskResult, analyze: analyzeOpNoteRisk, clearResult: clearOpNoteRiskResult } = useOpNoteRiskAnalysis();

  const performedAuthorizedLines = useMemo(() => (
    cptLines.filter(line => line.section !== 'secondary' || (ancillaryToggles[line.code] ?? true))
  ), [ancillaryToggles, cptLines]);

  const discoveryLines = useMemo<CaseCptLine[]>(() => (
    addedDiscoveries.map(discovery => ({
      code: discovery.cpt,
      description: discovery.name,
      modifiers: [],
      section: 'secondary' as const,
      fromTemplate: false,
    }))
  ), [addedDiscoveries]);

  const activePostOpLines = useMemo(() => {
    const seen = new Set<string>();
    return [...performedAuthorizedLines, ...discoveryLines].filter((line) => {
      if (seen.has(line.code)) return false;
      seen.add(line.code);
      return true;
    });
  }, [discoveryLines, performedAuthorizedLines]);

  const activePostOpCodes = useMemo(
    () => new Set(activePostOpLines.map(line => line.code)),
    [activePostOpLines],
  );
  const ncciLookupCodes = useMemo(() => (
    Array.from(new Set(cptLines.map(line => String(line.code || '').trim().toUpperCase())))
      .filter(code => /^[A-Z0-9]+$/.test(code))
  ), [cptLines]);

  const discoverySearchQuery = discoverySearch.trim();
  const { catalog: discoveryCatalog, results: cptSearchResults } = useCptSearch(discoverySearchQuery, {
    authorizedLines: cptLines,
    template,
    primaryCpt: cptLines[0]?.code,
    existingCodes: activePostOpCodes,
    limit: 8,
  });

  const defaultDiscoveryResults = useMemo(() => (
    buildNcciAwarePostOpDiscoverySuggestions({
      catalog: discoveryCatalog,
      primaryCpt: cptLines[0]?.code,
      authorizedLines: cptLines,
      template,
      activeCodes: activePostOpCodes,
      ncciPairRules,
      limit: 6,
    })
  ), [activePostOpCodes, cptLines, discoveryCatalog, ncciPairRules, template]);

  const searchResults = useMemo(() => {
    if (!discoverySearchQuery) return defaultDiscoveryResults;
    return rankPostOpSearchResultsWithNcci({
      results: cptSearchResults,
      primaryCpt: cptLines[0]?.code,
      authorizedLines: cptLines,
      activeCodes: activePostOpCodes,
      ncciPairRules,
    });
  }, [activePostOpCodes, cptLines, cptSearchResults, defaultDiscoveryResults, discoverySearchQuery, ncciPairRules]);

  const coverageResults = useMemo(
    () => evaluatePostOpDictationCoverage(activePostOpLines, dictationTranscript || ''),
    [activePostOpLines, dictationTranscript],
  );

  const needsCoverageFix = useMemo(
    () => coverageResults.flatMap(result =>
      result.requirements.filter(requirement => requirement.status !== 'documented'),
    ),
    [coverageResults],
  );

  const postOpAgentExperience = useMemo(() => buildPostOpDemoAgentExperience({
    patientName: selectedCase.patient_name,
    procedureName: template.template_name || selectedCase.scheduled_procedure || 'Selected procedure',
    cptCodes: activePostOpLines.map(line => line.code),
    authorizedCount: performedAuthorizedLines.length,
    discoveryCount: discoveryLines.length,
    hasDictation: Boolean(dictationTranscript?.trim()),
    coverageComplete: Boolean(dictationTranscript?.trim()) && needsCoverageFix.length === 0,
    missingCoverageLabels: needsCoverageFix.map(requirement => requirement.label),
  }), [
    activePostOpLines,
    dictationTranscript,
    discoveryLines.length,
    needsCoverageFix,
    performedAuthorizedLines.length,
    selectedCase.patient_name,
    selectedCase.scheduled_procedure,
    template.template_name,
  ]);

  useEffect(() => {
    setUseTemplateImplants(hasTemplateImplants);
  }, [hasTemplateImplants, selectedCase.id]);

  useEffect(() => {
    let cancelled = false;

    if (ncciLookupCodes.length === 0) {
      setNcciPairRules([]);
      return;
    }

    const loadNcciPairRules = async () => {
      const inList = ncciLookupCodes.join(',');
      const { data, error } = await supabase
        .from('cpt_pair_rules')
        .select('*')
        .eq('active', true)
        .or(`cpt_a.in.(${inList}),cpt_b.in.(${inList})`);

      if (cancelled) return;
      if (error) {
        setNcciPairRules(null);
        return;
      }
      setNcciPairRules((data || []) as NcciPairRuleInput[]);
    };

    setNcciPairRules([]);
    loadNcciPairRules();

    return () => {
      cancelled = true;
    };
  }, [ncciLookupCodes.join('|')]);

  const appendUniqueText = useCallback((currentText: string | null, addition: string) => {
    const trimmedAddition = addition.trim();
    if (!trimmedAddition) return currentText || '';

    const base = (currentText || '').trim();
    if (base.toLowerCase().includes(trimmedAddition.toLowerCase())) {
      return currentText || base;
    }

    return base ? `${base}\n\n${trimmedAddition}` : trimmedAddition;
  }, []);

  const postOpProofPacket = useMemo(() => buildPostOpConversationPacket({
    selectedCase,
    template,
    cptLines: activePostOpLines,
    laterality,
    transcript: dictationTranscript,
    bmiValue: bmiQualifiesForMod22 || isHighComplexity ? bmiValue : undefined,
    highComplexity: isHighComplexity,
  }), [selectedCase, template, activePostOpLines, laterality, dictationTranscript, bmiQualifiesForMod22, bmiValue, isHighComplexity]);

  const handleAcceptPostOpStatement = useCallback((statement: string, question: ClinicalQuestion) => {
    const trimmed = statement.trim();
    if (!trimmed) return;

    setConversationStatements(prev => {
      if (prev.some(existing => existing.trim().toLowerCase() === trimmed.toLowerCase())) return prev;
      return [...prev, trimmed];
    });
    setDictationTranscript(prev => appendUniqueText(prev, trimmed));
    if (question.id.includes('modifier-22')) {
      setIsHighComplexity(true);
    }
  }, [appendUniqueText]);

  const extractModifierFromFix = useCallback((text: string) => {
    const modifierMatch = text.match(/\b(XS|XE|XP|XU|RT|LT|59|22|51|80)\b/i);
    return modifierMatch?.[1]?.toUpperCase() ?? null;
  }, []);

  const handleApplySuggestedFix = useCallback((issue: ActionableIssue) => {
    if (!issue.proposed_fix_data) return;

    setDictationTranscript(prev => appendUniqueText(prev, issue.proposed_fix_data));
    toast.success('Accepted correction added to the operative narrative');
  }, [appendUniqueText]);

  const handleAppendAddendum = useCallback((issue: ActionableIssue, text: string) => {
    const nextText = text || issue.proposed_fix_data || '';
    if (!nextText.trim()) return;

    setDictationTranscript(prev => appendUniqueText(prev, nextText));
    toast.success('Addendum appended to the operative narrative');
  }, [appendUniqueText]);

  const handleApplyCodeFix = useCallback((issue: ActionableIssue) => {
    if (!issue.code || !issue.proposed_fix_data) return;

    const nextModifier = extractModifierFromFix(issue.proposed_fix_data);
    if (!nextModifier) {
      toast.error('Could not determine the required modifier change.');
      return;
    }

    const currentLine = cptLines.find(line => line.code === issue.code);
    const currentModifiers = currentLine?.modifiers ?? [];
    const modifierFamilies = ['59', 'XS', 'XE', 'XP', 'XU', 'RT', 'LT', '22', '51', '80'];
    const nextModifiers = [
      ...currentModifiers.filter(mod => {
        if (mod === nextModifier) return false;
        if (modifierFamilies.includes(nextModifier) && modifierFamilies.includes(mod)) {
          return !['59', 'XS', 'XE', 'XP', 'XU'].includes(nextModifier) || !['59', 'XS', 'XE', 'XP', 'XU'].includes(mod);
        }
        return true;
      }),
      nextModifier,
    ];

    setOpNoteResponse(prev => {
      if (!prev) return prev;

      const applyToCode = (codeEntry: typeof prev.primary_cpt) =>
        codeEntry.code === issue.code
          ? { ...codeEntry, modifiers: Array.from(new Set(nextModifiers)) }
          : codeEntry;

      return {
        ...prev,
        primary_cpt: applyToCode(prev.primary_cpt),
        additional_cpts: prev.additional_cpts.map(applyToCode),
      };
    });

    toast.success(`Applied correction to CPT ${issue.code}`);
  }, [cptLines, extractModifierFromFix]);

  const runPostOpAudit = useCallback(async (response: OpNoteResponse) => {
    if (perfectWorldDemo) {
      return true;
    }

    const operativeNote = response.optimized_note?.trim() || dictationTranscript || narrativeText;
    const billedCptCodes = [response.primary_cpt, ...response.additional_cpts].filter(code => code?.code);

    const result = await analyzeOpNoteRisk({
      operativeNote,
      billedCptCodes,
      icd10Codes: response.icd10_codes,
      patientDemographics: {
        name: selectedCase.patient_name,
        dob: selectedCase.patient_dob || '',
        mrn: selectedCase.patient_mrn || '',
        payer: selectedCase.payer_name || '',
        plan: selectedCase.plan_name || '',
      },
      procedureDescription: template.template_name,
    });

    if (!result) return true;

    if (result.verdict === 'BLOCKED') {
      toast.warning('Post-op note can be strengthened', {
        description: 'Suggested corrections are shown for one-click cleanup, but surgeon review is not blocked.',
      });
      return true;
    }

    clearOpNoteRiskResult();
    return true;
  }, [analyzeOpNoteRisk, clearOpNoteRiskResult, dictationTranscript, narrativeText, perfectWorldDemo, selectedCase.patient_dob, selectedCase.patient_mrn, selectedCase.patient_name, selectedCase.payer_name, selectedCase.plan_name, template.template_name]);

  const buildFallbackResponse = useCallback((): OpNoteResponse => {
    const primaryLine = activePostOpLines.find(line => line.section === 'primary') || activePostOpLines[0];
    const primaryModifiers = [
      ...(primaryLine?.modifiers || []),
      ...(reviewInputState.isHighComplexity ? ['22'] : []),
    ];
    const additionalLines = activePostOpLines.filter(line => line !== primaryLine);
    const diagnosisCodes = (template.diagnosis_codes || []).map(diagnosis => diagnosis.code).filter(Boolean);
    const highComplexityParagraph = reviewInputState.isHighComplexity
      ? `\n\nThis procedure involved increased complexity due to morbid obesity (BMI ${bmiValue.toFixed(1)}). Extensive adipose tissue required prolonged, tedious dissection, specialized retraction, and increased operative time by approximately 30 minutes beyond a standard case.`
      : '';
    const operativeNarrative = buildDeterministicShoulderOperativeNote({
      selectedCase,
      template,
      cptLines: activePostOpLines,
      laterality,
      positioning: defaultPositioning,
      ebl: `${ebl} mL`,
      operativeTime: `${opTime} mins`,
      dictation: dictationTranscript || narrativeText,
      highComplexityParagraph,
      varianceText,
      implants: templateImplants,
    });

    const fallbackResponse: OpNoteResponse = {
      optimized_note: operativeNarrative,
      audit_risk_score: 'MEDIUM',
      clean_claim_probability: 85,
      primary_cpt: {
        code: primaryLine?.code || '',
        description: primaryLine?.description || template.template_name,
        modifiers: Array.from(new Set(primaryModifiers)),
      },
      additional_cpts: additionalLines.map(line => ({
        code: line.code,
        description: line.description,
        modifiers: line.modifiers,
      })),
      icd10_codes: diagnosisCodes,
      documentation_gaps: [],
      evidence_payloads: [],
      implants_and_biologics: templateImplants.map(implant => ({
        name: implant.description,
        quantity: implant.qty,
        catalog_id: implant.catalog_id || '',
      })),
      financial_impact: {
        total_estimated_payout: 0,
        facility_revenue: 0,
        professional_revenue: 0,
        summary_text: 'Generating full reimbursement analysis...',
      },
      warnings: [],
      reconciliation_engine_version: 'local-fallback',
    };
    return perfectWorldDemo
      ? normalizeDemoPostOpResponse(fallbackResponse, selectedCase.id)
      : fallbackResponse;
  }, [activePostOpLines, bmiValue, defaultPositioning, dictationTranscript, ebl, laterality, narrativeText, opTime, perfectWorldDemo, reviewInputState.isHighComplexity, selectedCase, template, template.diagnosis_codes, template.template_name, templateImplants, varianceText]);

  // Overlay persists until BOTH: (1) API responded, AND (2) minimum time elapsed
  useEffect(() => {
    if (isGenerating && pendingResponse && overlayMinElapsed) {
      setOpNoteResponse(pendingResponse);
      setPendingResponse(null);
      setShowOverlay(false);
      setIsGenerating(false);
      setOverlayMinElapsed(false);
      setShowReview(true);
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      document.querySelector('[data-wizard-scroll-target]')?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      toast.success('Op note generated — ready for review.');
    }
  }, [isGenerating, pendingResponse, overlayMinElapsed]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    };
  }, []);

  const handleSign = useCallback(async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isGenerating) return; // prevent double-clicks
    if (!perfectWorldDemo && qCodeLine && !woundCareValid) {
      toast.warning('Skin substitute reconciliation can be strengthened', {
        description: 'Defect and waste units can be completed in Advanced Details; the operative note can still be reviewed.',
      });
    }
    setReviewInputState({
      ebl,
      operativeTime: opTime,
      isHighComplexity,
    });
    setIsGenerating(true);
    setShowOverlay(true);
    setOverlayMinElapsed(false);
    setPendingResponse(null);
    setOpNoteResponse(null);
    setShowReview(false);

    // Start minimum overlay duration (matches ~3 full phrase cycles)
    if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    overlayTimerRef.current = setTimeout(() => {
      setOverlayMinElapsed(true);
    }, 14400); // 14.4s = 3 × 4.8s phrase cycles

    const lateralityLabel = laterality === 'right' ? 'right' : laterality === 'left' ? 'left' : 'bilateral';
    const highComplexityParagraph = isHighComplexity
      ? `This procedure involved increased complexity due to morbid obesity (BMI ${bmiValue.toFixed(1)}). Extensive adipose tissue required prolonged, tedious dissection, specialized retraction, and increased operative time by approximately 30 minutes beyond a standard case.`
      : '';
    const dictationForGeneration = buildDeterministicShoulderOperativeNote({
      selectedCase,
      template,
      cptLines: activePostOpLines,
      laterality,
      positioning: defaultPositioning,
      ebl: `${ebl} mL`,
      operativeTime: `${opTime} mins`,
      dictation: dictationTranscript || narrativeText,
      highComplexityParagraph,
      varianceText,
      implants: templateImplants,
    });
    const payload = {
      input_type: 'smart_builder',
      auth_id: selectedCase.id,
      patient_name: selectedCase.patient_name,
      patient_dob: selectedCase.patient_dob || null,
      patient_mrn: selectedCase.patient_mrn || null,
      template_name: template.template_name,
      authorized_cpts: activePostOpLines.map(l => l.code),
      cpt_details: activePostOpLines.map(l => ({ code: l.code, description: l.description, modifiers: l.modifiers })),
      diagnosis_codes: template.diagnosis_codes || [],
      payer_name: selectedCase.payer_name || null,
      plan_name: selectedCase.plan_name || null,
      laterality: lateralityLabel,
      positioning: defaultPositioning,
      ebl: `${ebl} mL`,
      operative_time: `${opTime} mins`,
      dictation: dictationForGeneration,
      high_complexity_bmi: isHighComplexity,
      bmi_value: bmiValue,
      implants: useTemplateImplants ? templateImplants : [],
      variance: varianceText || null,
      save_variance_permanent: savePermanent,
      surgeon_clarifications: surgeonClarifications,
    };

    try {
      console.log('[PostOpNote] Sending to generate-op-note:', payload);

      const response = await fetch(`${LOCAL_SUPABASE_URL}/functions/v1/generate-op-note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: LOCAL_SUPABASE_KEY,
          Authorization: `Bearer ${LOCAL_SUPABASE_KEY}`,
        },
        body: JSON.stringify(payload),
      });

      let data: any = null;
      try { data = await response.json(); } catch { data = null; }

      if (!response.ok) throw new Error(data?.error || `generate-op-note failed (${response.status})`);
      if (!data) throw new Error('No data returned');

      const fallbackResponse = buildFallbackResponse();
      const serviceResponse = normalizeOpNoteResponse(data);
      const normalized = perfectWorldDemo
        ? normalizeDemoPostOpResponse(serviceResponse, selectedCase.id)
        : serviceResponse;
      normalized.optimized_note = ensureOperativeTemplateBlocks(
        normalized.optimized_note,
        activePostOpLines,
        fallbackResponse.optimized_note,
        dictationForGeneration,
      );
      const passedAudit = await runPostOpAudit(normalized);
      if (!passedAudit) {
        setShowOverlay(false);
        setIsGenerating(false);
        setOverlayMinElapsed(false);
        if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
        return;
      }
      setPendingResponse(normalized);
    } catch (err: any) {
      console.error('generate-op-note error:', err);
      // On error, use fallback so user isn't stuck on overlay forever
      setPendingResponse(buildFallbackResponse());
    }
  }, [selectedCase, template, activePostOpLines, laterality, defaultPositioning, ebl, opTime, dictationTranscript, narrativeText, isHighComplexity, bmiValue, varianceText, useTemplateImplants, templateImplants, savePermanent, surgeonClarifications, runPostOpAudit, qCodeLine, woundCareValid, buildFallbackResponse, perfectWorldDemo]);

  const handleAddCatalogDiscovery = useCallback((entry: PostOpCptCatalogEntry) => {
    setAddedDiscoveries(prev => {
      if (prev.some(discovery => discovery.cpt === entry.code) || activePostOpCodes.has(entry.code)) {
        return prev;
      }
      return [
        ...prev,
        {
          cpt: entry.code,
          name: entry.description,
          noteText: '',
        },
      ];
    });
    setDiscoverySearch('');
    toast.success(`Added CPT ${entry.code}`, {
      description: 'Smart Dictation requirements updated.',
    });
  }, [activePostOpCodes]);

  const handleUseGeneratedDictation = useCallback(() => {
    const generated = buildGeneratedPostOpDictation({
      patientName: selectedCase.patient_name,
      procedureName: template.template_name,
      activeLines: activePostOpLines,
      laterality,
      positioning: defaultPositioning,
      ebl: `${ebl} mL`,
      operativeTime: `${opTime} mins`,
    });
    setDictationTranscript(generated);
    toast.success('Smart dictation generated', {
      description: 'Review or edit the operative details before generating the final note.',
    });
  }, [activePostOpLines, defaultPositioning, ebl, laterality, opTime, selectedCase.patient_name, template.template_name]);

  const handleUseSuggestedLine = useCallback((requirement: PostOpCoverageRequirement) => {
    setDictationTranscript(prev => appendSuggestedDictationLine(prev || '', requirement));
    toast.success('Suggested sentence added', {
      description: `Updated CPT ${requirement.cptCode} documentation.`,
    });
  }, []);

  // Dictation mode: show embedded DictationScreen
  if (dictationMode) {
    const authorizedCptsForDictation = activePostOpLines.map(l => ({
      code: l.code,
      description: l.description,
    }));

    return (
      <div className="space-y-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDictationMode(false)}
          className="gap-2 text-muted-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Smart Builder
        </Button>
        <DictationScreen
          embedded={true}
          authorizationId={selectedCase.id || null}
          caseNumber={selectedCase.encounter_id || selectedCase.patient_mrn || 'OR-CASE'}
          surgeonId="current-surgeon"
          authorizedCpts={authorizedCptsForDictation}
          onComplete={(data) => {
            setDictationTranscript(data.transcript);
            setDictationMode(false);
            toast.success('Dictation captured', {
              description: `${data.transcript.split(/\s+/).length} words captured. Ready to generate operative note.`,
            });
          }}
          onCancel={() => setDictationMode(false)}
        />
      </div>
    );
  }

  if (showReview && opNoteResponse) {
    return (
      <PostOpReviewDashboard
        response={opNoteResponse}
        caseId={selectedCase.id}
        patientName={selectedCase.patient_name}
        patientDob={selectedCase.patient_dob || ''}
        patientMrn={selectedCase.patient_mrn || ''}
        patientSex={selectedCase.patient_sex}
        payerName={selectedCase.payer_name}
        facilityLocation={selectedCase.facility_name}
        caseEncounterNo={selectedCase.encounter_id}
        onBack={() => setShowReview(false)}
        onFinalize={() => {}}
        onDeferToOfficeManager={onCaseDeferred}
        onDemoResetCase={onDemoResetCase}
        initialEbl={reviewInputState.ebl}
        initialOpTime={reviewInputState.operativeTime}
        initialHighComplexity={reviewInputState.isHighComplexity}
        initialLaterality={laterality}
        initialPositioning={defaultPositioning}
        initialAnesthesia="General"
        initialImplantsVerified={useTemplateImplants && hasTemplateImplants}
        initialConservativeTherapy={!urgentBypassTemplate}
        initialAssistant={defaultAssistantStatement}
      />
    );
  }

  const simplifiedComposerEnabled = true;
  if (simplifiedComposerEnabled) {
    return (
      <>
        <GenerationOverlay show={showOverlay} />
        <div className="space-y-4">
          {opNoteRiskResult && (
            <DenialRiskAlertPanel
              result={opNoteRiskResult}
              onDismiss={clearOpNoteRiskResult}
              onApplyCodeFix={handleApplyCodeFix}
              onApplySuggestedFix={handleApplySuggestedFix}
              onAppendAddendum={handleAppendAddendum}
              mode="post-op"
            />
          )}

          <Card className="border-primary/20 bg-primary/[0.03]">
            <CardContent className="p-5 space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-primary" />
                    <h2 className="text-base font-semibold text-foreground">Post-Op Composer</h2>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Confirm the authorized work, add any intra-op discoveries, then generate or dictate the note.
                  </p>
                </div>
                <Badge className="w-fit bg-emerald-50 text-emerald-700 border-emerald-200">
                  Demo-safe smart builder
                </Badge>
              </div>

              <div className="grid gap-3 md:grid-cols-4">
                <InfoField icon={User} label="Patient" value={selectedCase.patient_name} />
                <InfoField icon={Calendar} label="DOB" value={formatPatientDob(selectedCase.patient_dob)} />
                <InfoField icon={Hash} label="MRN" value={selectedCase.patient_mrn || '—'} />
                <InfoField icon={Shield} label="Facility" value={selectedCase.facility_name || facility?.facility_name || '—'} />
              </div>
            </CardContent>
          </Card>

          {perfectWorldDemo && (
            <DemoAgenticAssistant experience={postOpAgentExperience} />
          )}

          <Card className="border-border/60">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">1. Patient + Authorized Bundle</h3>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                  Pre-Auth Cleared
                </Badge>
              </div>

              <div className="grid gap-2">
                {cptLines.map((line) => {
                  const isSecondary = line.section === 'secondary';
                  const performed = !isSecondary || (ancillaryToggles[line.code] ?? true);
                  return (
                    <div
                      key={`${line.code}-${line.modifiers.join('-')}`}
                      className={`flex flex-col gap-3 rounded-lg border p-3 transition-colors sm:flex-row sm:items-center sm:justify-between ${
                        performed ? 'border-border/60 bg-background' : 'border-muted bg-muted/20 opacity-60'
                      }`}
                    >
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="font-mono">{line.code}</Badge>
                          {line.modifiers.map(modifier => (
                            <Badge key={modifier} className="bg-sky-50 text-sky-700 border-sky-200">
                              {modifier}
                            </Badge>
                          ))}
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            Pre-Auth Cleared
                          </Badge>
                        </div>
                        <p className="text-sm font-medium text-foreground">{line.description}</p>
                      </div>
                      {isSecondary ? (
                        <div className="flex items-center gap-2 self-start sm:self-center">
                          <span className="text-xs font-medium text-muted-foreground">
                            {performed ? 'Performed' : 'Not performed'}
                          </span>
                          <Switch
                            checked={performed}
                            onCheckedChange={(value) =>
                              setAncillaryToggles(prev => ({ ...prev, [line.code]: !!value }))
                            }
                            className="h-7 w-12 data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
                          />
                        </div>
                      ) : (
                        <Badge variant="secondary">Primary</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">2. Intra-Op Discoveries</h3>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={discoverySearch}
                  onChange={event => setDiscoverySearch(event.target.value)}
                  placeholder="Smart Search by CPT code or procedure, e.g. 29824 or distal clavicle"
                  className="h-12 pl-9 text-sm"
                />
              </div>

              <div className="grid gap-2 md:grid-cols-2">
                {searchResults.slice(0, 6).map(entry => (
                  <button
                    key={`${entry.code}-${entry.source}`}
                    type="button"
                    onClick={() => handleAddCatalogDiscovery(entry)}
                    className="rounded-lg border border-border/60 bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-accent/30"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono">{entry.code}</Badge>
                      {entry.plausible && (
                        <Badge className="bg-primary/10 text-primary border-primary/20">Plausible</Badge>
                      )}
                      {entry.reviewRequired && (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200">Needs RCM Review</Badge>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-medium text-foreground">{entry.description}</p>
                  </button>
                ))}
              </div>

              {discoveryLines.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-900">
                    Added intra-op discoveries
                  </p>
                  <div className="space-y-2">
                    {discoveryLines.map(line => (
                      <div key={line.code} className="flex items-center justify-between gap-3 rounded-md bg-white/80 px-3 py-2">
                        <div className="min-w-0">
                          <span className="font-mono text-xs font-semibold">{line.code}</span>
                          <span className="ml-2 text-sm text-foreground">{line.description}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setAddedDiscoveries(prev => prev.filter(item => item.cpt !== line.code))}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardContent className="p-5 space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Wand2 className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">3. Smart Dictation</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={handleUseGeneratedDictation} className="gap-2">
                    <Sparkles className="h-4 w-4" />
                    Use Generated Dictation
                  </Button>
                  <Button type="button" onClick={() => setDictationMode(true)} className="gap-2">
                    <Mic className="h-4 w-4" />
                    Dictate
                  </Button>
                </div>
              </div>

              <Textarea
                value={dictationTranscript || ''}
                onChange={event => setDictationTranscript(event.target.value)}
                placeholder="Use generated dictation, dictate, or type the operative details here. LINK checks the CPT coverage as the text appears."
                className="min-h-[180px] text-sm leading-7"
              />

              <PostOpCoverageChecklist
                coverageResults={coverageResults}
                hasTranscript={Boolean((dictationTranscript || '').trim())}
                onUseSuggestedLine={handleUseSuggestedLine}
              />

              <div className="rounded-lg border border-border/60 bg-muted/20">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen(!advancedOpen)}
                  className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-foreground"
                >
                  Advanced Details
                  {advancedOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {advancedOpen && (
                  <div className="space-y-4 border-t px-4 py-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          Estimated Blood Loss
                        </label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={ebl}
                            onChange={event => setEbl(event.target.value)}
                            className="h-11 w-24 text-center text-base font-semibold"
                          />
                          <span className="text-sm text-muted-foreground">mL</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          Operative Time
                        </label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={opTime}
                            onChange={event => setOpTime(event.target.value)}
                            className="h-11 w-24 text-center text-base font-semibold"
                          />
                          <span className="text-sm text-muted-foreground">mins</span>
                        </div>
                      </div>
                    </div>

                    {hasTemplateImplants ? (
                      <label className="flex cursor-pointer items-start gap-3">
                        <Checkbox
                          checked={useTemplateImplants}
                          onCheckedChange={(value) => setUseTemplateImplants(!!value)}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="text-sm font-medium text-foreground">
                            Use template implants: {templateImplants.map(implant => `(${implant.qty}) ${implant.description}`).join(', ')}
                          </span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            {templateImplants.map(implant => implant.catalog_id).filter(Boolean).join(', ') || 'Implants verified from template'}
                          </span>
                        </span>
                      </label>
                    ) : (
                      <p className="text-sm text-muted-foreground">No default implants on this procedure template.</p>
                    )}

                    {bmiQualifiesForMod22 && (
                      <div className="flex items-center justify-between rounded-lg border bg-background p-3">
                        <div>
                          <p className="text-sm font-medium">Modifier -22 BMI complexity</p>
                          <p className="text-xs text-muted-foreground">BMI {bmiValue.toFixed(1)} detected from intake.</p>
                        </div>
                        <Switch checked={isHighComplexity} onCheckedChange={setIsHighComplexity} />
                      </div>
                    )}

                    {!perfectWorldDemo && qCodeLine && (
                      <WoundCareMatrix
                        totalUnitsBilled={qCodeBilledUnits}
                        onValidate={handleWoundCareValidate}
                      />
                    )}
                  </div>
                )}
              </div>

              {needsCoverageFix.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                  {needsCoverageFix.length} documentation item{needsCoverageFix.length !== 1 ? 's' : ''} can be strengthened with one-click suggested language. This will not block the surgeon.
                </div>
              )}
            </CardContent>
          </Card>

          <div className="sticky bottom-0 z-20 -mx-1 border-t bg-background/95 px-1 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleUseGeneratedDictation}
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Refresh Smart Dictation
              </Button>
              <Button
                onClick={handleSign}
                disabled={isGenerating || isAuditingNote}
                className="h-12 gap-2 bg-blue-600 px-8 text-base font-semibold text-white hover:bg-blue-700"
              >
                {isGenerating || isAuditingNote ? (
                  <>
                    <Sparkles className="h-4 w-4 animate-pulse" />
                    Preparing Note...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Review Operative Note
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <GenerationOverlay show={showOverlay} />
      <div className="space-y-5">
      {opNoteRiskResult && (
        <DenialRiskAlertPanel
          result={opNoteRiskResult}
          onDismiss={clearOpNoteRiskResult}
          onApplyCodeFix={handleApplyCodeFix}
          onApplySuggestedFix={handleApplySuggestedFix}
          onAppendAddendum={handleAppendAddendum}
          mode="post-op"
        />
      )}

      {/* ── 1. LOCKED HEADER ── */}
      <Card className="border-primary/30 bg-primary/[0.04]">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-primary tracking-wide uppercase">
              Imported Pre-Authorization Data
            </h3>
            <Badge variant="outline" className="ml-auto text-[10px] border-primary/30 text-primary font-medium">
              Read-Only
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-6 text-sm">
            <InfoField icon={User} label="Patient" value={selectedCase.patient_name} />
            <InfoField icon={Calendar} label="DOB" value={formatPatientDob(selectedCase.patient_dob)} />
            <InfoField icon={Hash} label="MRN" value={selectedCase.patient_mrn || '—'} />
            <InfoField icon={Shield} label="Pre-Auth #" value={selectedCase.encounter_id || '—'} />
            <InfoField icon={Stethoscope} label="Authorized CPT" value={cptLines.map(l => `${l.code}${l.modifiers.length ? '-' + l.modifiers.join(',') : ''}`).join(', ') || '—'} />
            <InfoField icon={Syringe} label="Laterality" value={laterality === 'right' ? `Right ${procedureSiteLabel}` : laterality === 'left' ? `Left ${procedureSiteLabel}` : laterality === 'bilateral' ? `Bilateral ${procedureSiteLabel}` : '—'} />
          </div>
        </CardContent>
      </Card>

      <SurgeonConversationPanel
        packet={postOpProofPacket}
        acceptedStatements={conversationStatements}
        onAcceptStatement={handleAcceptPostOpStatement}
      />

      {/* ── 2. MEDICAL NECESSITY (Read-Only) ── */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-primary tracking-wide uppercase">
              Medical Necessity — Verified
            </h3>
          </div>
          <div className="space-y-3">
            {urgentBypassTemplate ? (
              <ReadOnlyCheck label="Urgent traumatic indication supports conservative-care bypass" />
            ) : (
              <ReadOnlyCheck label="Formal conservative therapy and clinical plateau are documented" />
            )}
            <ReadOnlyCheck label="Diagnostic and clinical record support the authorized procedure" />
          </div>
        </CardContent>
      </Card>

      {/* ── 3. OPERATIVE NOTE INPUT ── */}
      <Card className={`border-border/60 ${dictationTranscript ? 'border-primary/30 bg-primary/[0.02]' : ''}`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">
                {dictationTranscript ? 'Surgeon Dictation' : 'Master Procedure Template'}
                <span className="ml-1.5 text-muted-foreground font-normal">({primaryCptDisplay})</span>
              </h3>
              {dictationTranscript && (
                <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px] gap-1">
                  <Mic className="w-3 h-3" />
                  Dictated
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {!dictationTranscript && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDictationMode(true)}
                        className="gap-1.5 text-xs h-8 border-primary/30 text-primary hover:bg-primary/10"
                      >
                        <Mic className="w-3.5 h-3.5" />
                        Dictate Note
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p className="text-xs">Record your operative dictation to generate the note</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {dictationTranscript && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDictationMode(true)}
                  className="gap-1.5 text-xs h-8 text-muted-foreground"
                >
                  <Mic className="w-3.5 h-3.5" />
                  Re-dictate
                </Button>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleSpeak(dictationTranscript || narrativeText)}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg transition-all hover:bg-muted text-muted-foreground hover:text-foreground"
                      aria-label={isSpeaking ? 'Stop reading' : 'Listen to note'}
                    >
                      {isSpeaking
                        ? <Square className="w-4 h-4 text-destructive" />
                        : <Volume2 className="w-4 h-4" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p className="text-xs">{isSpeaking ? 'Stop Reading' : 'Listen to Note'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          <Separator className="mb-4" />

          <div className="rounded-xl border border-border/40 bg-muted/30 p-5">
            {dictationTranscript ? (
              <>
                <p className="text-sm leading-[1.85] text-foreground/90 whitespace-pre-line">
                  {dictationTranscript}
                </p>
                <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-border/30">
                  {dictationTranscript.split(/\s+/).filter(Boolean).length} words captured via dictation
                </p>
              </>
            ) : (
              <p className="text-sm leading-[1.85] text-foreground/90 whitespace-pre-line">
                {narrativeText}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── 3a. AI RECONCILIATION — gap-find vs pre-auth, ask surgeon for clarifications ── */}
      {dictationTranscript && (
        <AIReconciliationPanel
          authorizedCpts={cptLines.map(l => ({ code: l.code, description: l.description }))}
          transcript={dictationTranscript}
          onClarificationsReady={(clarifications: SurgeonClarification[]) => {
            setSurgeonClarifications(clarifications);
            toast.success('Clarifications captured', {
              description: `${clarifications.length} answer${clarifications.length !== 1 ? 's' : ''} will be woven into the bulletproof note when you generate.`,
            });
          }}
        />
      )}

      {/* ── 3b. PATIENT FACTORS & COMPLEXITY ──
          Only renders when intake BMI qualifies for Modifier -22 (>= 40). For
          patients below threshold the surgeon never sees this section — no
          friction, no manual toggle, no risk of accidentally adding -22 when
          it isn't supported. */}
      {bmiQualifiesForMod22 && (
      <Card className="border-border/60">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-primary" />
            Patient Factors &amp; Complexity
          </h3>

          <div className="bg-blue-50 text-blue-800 text-sm px-4 py-3 rounded-md mb-5 flex items-start gap-2">
            <Info className="text-blue-500 shrink-0 mt-0.5" size={16} />
            <span>
              <strong>Auto-detected from intake:</strong> Patient BMI {bmiValue.toFixed(1)} qualifies for Modifier -22 (morbid obesity, BMI &ge; {MOD_22_BMI_THRESHOLD}). The required legal justification will be auto-injected into the operative note. Toggle off only if the case did not in fact require substantially greater work than the CPT baseline.
            </span>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-muted/20">
            <div className="min-w-0 pr-4">
              <span className="text-sm font-medium text-foreground">
                Patient BMI {bmiValue.toFixed(1)} (Morbid Obesity / Modifier -22)
              </span>
            </div>
            <Switch
              checked={isHighComplexity}
              onCheckedChange={setIsHighComplexity}
              className="h-7 w-12 shrink-0 data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
            />
          </div>
          {isHighComplexity && (
            <div className="flex items-center gap-3 mt-3 px-4">
              <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">Patient BMI:</span>
              <input
                type="number"
                step="0.1"
                min="30"
                max="99"
                value={bmiValue}
                onChange={(e) => setBmiValue(parseFloat(e.target.value) || 40)}
                className="w-20 h-7 text-sm font-semibold bg-white border border-border rounded px-2"
              />
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* ── 4. PROCEDURES PERFORMED & DISCOVERIES ──
          The toggle list is built from THIS case's authorized secondary CPTs
          (cptLines.section === 'secondary') so the surgeon never sees codes that
          weren't actually pre-authorized. The previous hard-coded shoulder list
          showed "Pre-Auth Cleared" badges for codes that may not have been on
          the auth, which was misleading and a denial risk. */}
      {(() => {
        const authorizedAncillaries = cptLines.filter(l => l.section === 'secondary');
        if (authorizedAncillaries.length === 0) return null;
        return (
      <Card className="border-border/60 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-primary" />
            Procedures Performed &amp; Discoveries
          </h3>

          <div className="bg-blue-50 text-blue-800 text-sm px-4 py-2 rounded-md mb-4 flex items-start gap-2">
            <Info className="text-blue-500 shrink-0 mt-0.5" size={16} />
            <span>The following ancillary procedures were successfully pre-authorized for this case. Toggle off any that were not performed.</span>
          </div>

          <div className="space-y-3">
            {authorizedAncillaries.map((proc) => {
              const isOn = ancillaryToggles[proc.code] ?? true;
              return (
                <div
                  key={proc.code}
                  className={`flex items-center justify-between p-3.5 rounded-lg border border-border/50 bg-muted/20 transition-all duration-300 ${!isOn ? 'opacity-50 grayscale' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground">{proc.description}</span>
                      <Badge variant="outline" className="text-[10px] font-mono">{proc.code}</Badge>
                      {isOn ? (
                        <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                          <ShieldCheck size={12} />
                          Pre-Auth Cleared
                        </span>
                      ) : (
                        <span className="bg-muted text-muted-foreground text-xs font-medium px-2 py-0.5 rounded-full">
                          Aborted
                        </span>
                      )}
                    </div>
                    <span className={`text-xs mt-0.5 block ${isOn ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                      {isOn ? 'Performed' : 'Not Performed / Aborted'}
                    </span>
                  </div>
                  <Switch
                    checked={isOn}
                    onCheckedChange={(v) =>
                      setAncillaryToggles(prev => ({ ...prev, [proc.code]: !!v }))
                    }
                    className="h-7 w-12 data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
                  />
                </div>
              );
            })}
          </div>

          <div className="border-t border-border/40 my-4" />

          <button
            onClick={() => setDiscoveryOpen(true)}
            className="w-full h-12 rounded-lg border-2 border-dashed border-muted-foreground/30 text-muted-foreground hover:bg-muted/40 hover:border-muted-foreground/50 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Intra-Op Discovery
          </button>

          {addedDiscoveries.length > 0 && (
            <ul className="space-y-2 mt-2">
              {addedDiscoveries.map((d) => (
                <li key={d.cpt} className="flex items-center gap-2 text-sm p-2.5 rounded-md bg-amber-50 border border-amber-200">
                  <Badge variant="outline" className="text-[10px]">{d.cpt}</Badge>
                  <span className="text-foreground">{d.name}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
        );
      })()}

      <IntraOpDiscoveryModal
        open={discoveryOpen}
        onClose={() => setDiscoveryOpen(false)}
        onAdd={(proc) => {
          setAddedDiscoveries(prev => [...prev, proc]);
        }}
      />

      {/* ── 5. QUICK VARIABLES & IMPLANTS ── */}
      <Card className="border-border/60">
        <CardContent className="p-5 space-y-5">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Syringe className="w-4 h-4 text-primary" />
            Quick Variables & Implants
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Estimated Blood Loss
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={ebl}
                  onChange={e => setEbl(e.target.value)}
                  className="h-11 w-24 text-center font-semibold text-base"
                />
                <span className="text-sm text-muted-foreground">mL</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Operative Time
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={opTime}
                  onChange={e => setOpTime(e.target.value)}
                  className="h-11 w-24 text-center font-semibold text-base"
                />
                <span className="text-sm text-muted-foreground">mins</span>
              </div>
            </div>
          </div>

          <Separator />

          {hasTemplateImplants ? (
            <div className="flex items-start gap-3">
              <Checkbox
                id="implant-template"
                checked={useTemplateImplants}
                onCheckedChange={(v) => setUseTemplateImplants(!!v)}
                className="mt-0.5"
              />
              <label htmlFor="implant-template" className="cursor-pointer">
                <span className="text-sm font-medium text-foreground">
                  Use Master Template: {templateImplants.map(implant => `(${implant.qty}) ${implant.description}`).join(', ')}
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {templateImplants.map(implant => implant.catalog_id).filter(Boolean).join(', ') || 'Implants verified from template'}
                </span>
              </label>
            </div>
          ) : (
            <div className="rounded-lg border border-border/50 bg-muted/20 px-4 py-3">
              <span className="text-sm font-medium text-foreground">No default implants on this procedure template</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                Implant verification will stay out of the note unless the surgeon adds a variance.
              </span>
            </div>
          )}

          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowModify(!showModify)}
              className="gap-2 text-xs"
            >
              {showModify ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Modify
            </Button>

            {showModify && (
              <div className="mt-3 space-y-3 rounded-xl border border-amber-200/60 bg-amber-50/30 p-4">
                <label className="text-xs font-medium text-amber-800 uppercase tracking-wider">
                  Describe Variance
                </label>
                <Textarea
                  placeholder="e.g., Used 3 anchors instead of 2…"
                  value={varianceText}
                  onChange={e => setVarianceText(e.target.value)}
                  className="min-h-[80px] text-sm bg-background"
                />
                <label className="flex items-center gap-3 cursor-pointer">
                  <Switch
                    checked={savePermanent}
                    onCheckedChange={setSavePermanent}
                  />
                  <span className="text-xs text-foreground">
                    Save as permanent change to Master Template for future {primaryCptDisplay} procedures
                  </span>
                </label>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Wound Care Reconciliation Gate (skin substitute Q-codes) ── */}
      {!perfectWorldDemo && qCodeLine && (
        <WoundCareMatrix
          totalUnitsBilled={qCodeBilledUnits}
          onValidate={handleWoundCareValidate}
        />
      )}

      {/* ── 8. HERO ACTION BUTTON ── */}
      <div className="mt-8 mb-12 pb-[env(safe-area-inset-bottom)] px-1 sm:px-0 flex justify-end">
        <button
          onClick={handleSign}
          disabled={isGenerating || isAuditingNote || (!perfectWorldDemo && !!qCodeLine && !woundCareValid)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg h-14 px-8 md:px-12 rounded-xl shadow-lg hover:shadow-xl transition-all w-full md:w-auto flex items-center justify-center gap-3 disabled:opacity-60 disabled:pointer-events-none"
        >
          <Sparkles size={20} />
          Generate Operative Note
        </button>
      </div>
    </div>
    </>
  );
}

/* ── Helper sub-components ── */

function InfoField({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
        <Icon className="w-3.5 h-3.5 text-primary" />
      </div>
      <div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className="font-medium text-xs text-foreground">{value}</div>
      </div>
    </div>
  );
}

function ReadOnlyCheck({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary shrink-0">
        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
      </div>
      <span className="text-sm text-foreground">{label}</span>
    </div>
  );
}

function PostOpCoverageChecklist({
  coverageResults,
  hasTranscript,
  onUseSuggestedLine,
}: {
  coverageResults: PostOpCoverageResult[];
  hasTranscript: boolean;
  onUseSuggestedLine: (requirement: PostOpCoverageRequirement) => void;
}) {
  const totalRequirements = coverageResults.reduce((total, result) => total + result.requirements.length, 0);
  const documentedRequirements = coverageResults.reduce(
    (total, result) => total + result.requirements.filter(requirement => requirement.status === 'documented').length,
    0,
  );

  return (
    <div className="rounded-xl border border-border/60 bg-background p-4">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">CPT Coverage Checklist</p>
          <p className="text-xs text-muted-foreground">
            {hasTranscript
              ? `${documentedRequirements}/${totalRequirements || coverageResults.length} key points verified`
              : 'Use generated dictation or dictate to begin live coverage checks.'}
          </p>
        </div>
        {documentedRequirements > 0 && (
          <Badge className="w-fit bg-emerald-50 text-emerald-700 border-emerald-200">
            Updating live
          </Badge>
        )}
      </div>

      <div className="space-y-3">
        {coverageResults.map(result => (
          <div key={result.code} className="rounded-lg border border-border/50 p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="font-mono">{result.code}</Badge>
              <span className="text-sm font-medium text-foreground">{result.description}</span>
              {result.status === 'documented' && (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Covered</Badge>
              )}
              {result.status === 'needs_sentence' && (
                <Badge className="bg-amber-50 text-amber-700 border-amber-200">Needs one sentence</Badge>
              )}
              {result.status === 'review' && (
                <Badge className="bg-amber-50 text-amber-700 border-amber-200">Review wording</Badge>
              )}
              {result.status === 'no_rules' && (
                <Badge variant="secondary">No checklist rules</Badge>
              )}
            </div>

            {result.requirements.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                LINK will include this CPT in the final note; no local proof-map checklist exists yet.
              </p>
            ) : (
              <div className="space-y-2">
                {result.requirements.map(requirement => (
                  <div
                    key={requirement.id}
                    className={`flex flex-col gap-2 rounded-md px-3 py-2 sm:flex-row sm:items-center sm:justify-between ${
                      requirement.status === 'documented'
                        ? 'bg-emerald-50/80'
                        : requirement.status === 'review'
                          ? 'bg-amber-50'
                          : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex min-w-0 items-start gap-2">
                      {requirement.status === 'documented' ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      ) : requirement.status === 'review' ? (
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      ) : (
                        <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{requirement.label}</p>
                        {requirement.evidenceSnippet && (
                          <p className="truncate text-xs text-muted-foreground">
                            Evidence: {requirement.evidenceSnippet}
                          </p>
                        )}
                      </div>
                    </div>
                    {requirement.status !== 'documented' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 border-amber-300 text-amber-900 hover:bg-amber-100"
                        onClick={() => onUseSuggestedLine(requirement)}
                      >
                        Use Suggested Line
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
